package org.moxultimate.topwithmox.components.crud

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.moxultimate.topwithmox.theme.FontFamily

/** Wallpaper type options shared across all CRUD forms. */
val crudWallpaperTypes = listOf("Desktop", "Phone", "Vertical", "Widescreen")

/** Product label options. */
val crudProductLabels = listOf("Balanced", "Best Design", "Best Pick", "Budget Pick", "Editor's Choice")

/** App type options. */
val crudAppTypes = listOf(
    "AI",
    "Banking",
    "Business",
    "Commerce",
    "Earning",
    "Education",
    "Games",
    "Health",
    "Music",
    "Productivity",
    "Social Media",
    "Streaming",
)

/** Show / series content type options. */
val crudShowTypes = listOf(
    "Anime",
    "Contents",
    "Documentary",
    "Educational",
    "Interviews",
    "Movies",
    "Productivity",
    "Reality TV",
    "Series",
    "Short Film",
)

/** Product content (section) type options. */
val crudProductContentTypes = listOf(
    "Adapters",
    "Brackets",
    "Cables",
    "Chargers",
    "Desktop",
    "Earbuds",
    "Earphones",
    "Headphones",
    "HHD",
    "Keyboard",
    "Laptops",
    "Laptop Case",
    "Lights",
    "Mac Mini",
    "Memory",
    "Monitor",
    "Mouse",
    "Organizers",
    "Phones",
    "Phone Case",
    "Powerbank",
    "Printer",
    "Router",
    "SSD",
    "Speakers",
    "Webcam",
)

/** Tech / how-to content type options. */
val crudTechTypes = listOf(
    "Coding",
    "Guide",
    "Inventions",
    "News",
    "Setup",
    "Tips & Tricks",
    "Tools",
    "Tutorial",
)

/**
 * CRUDDropdown
 * Generic labelled dropdown that renders a native HTML select element.
 * Used wherever a fixed set of options should replace a free-text field.
 *
 * @param label      Field label shown above the select. Append " *" to mark it required.
 * @param options    Ordered list of selectable values.
 * @param selected   Currently selected value, or empty string when nothing is chosen.
 * @param placeholder Placeholder text shown as the disabled first option.
 * @param onSelected Callback with the newly chosen value.
 */
@Composable
fun CRUDDropdown(
    label: String,
    options: List<String>,
    selected: String,
    placeholder: String = "Select…",
    onSelected: (String) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.px),
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontFamily(FontFamily)
                .fontSize(12.px)
                .fontWeight(FontWeight.SemiBold)
                .color(Colors.Gray)
                .styleModifier { property("text-transform", "uppercase") }
                .letterSpacing(0.5.px),
        )
        Select(
            attrs = {
                style {
                    property("width", "100%")
                    property("height", "44px")
                    property("padding", "0 20px 0 14px")
                    property("background-color", "#ffffff")
                    property("border", "1px solid #d3d3d3")
                    property("border-radius", "10px")
                    property("outline", "none")
                    property("font-family", FontFamily.joinToString(", "))
                    property("font-size", "14px")
                    property("color", if (selected.isEmpty()) "#999" else "#262525")
                    property("cursor", "pointer")
                    property("appearance", "auto")
                }
                onChange { e ->
                    val v = e.target.asDynamic()?.value as? String ?: ""
                    onSelected(v)
                }
            }
        ) {
            Option(value = "", attrs = { attr("disabled", ""); if (selected.isEmpty()) attr("selected", "") }) {
                Text(placeholder)
            }
            options.forEach { opt ->
                Option(
                    value = opt,
                    attrs = { if (selected.equals(opt, ignoreCase = true)) attr("selected", "") }) { Text(opt) }
            }
        }
    }
}

/**
 * Labelled dropdown for selecting a wallpaper type.
 *
 * @param selected   Currently selected type string, or empty string when nothing is chosen.
 * @param onSelected Callback with the newly chosen type string.
 */
@Composable
fun CRUDTypeDropdown(selected: String, onSelected: (String) -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.px),
    ) {
        SpanText(
            text = "Type *",
            modifier = Modifier
                .fontFamily(FontFamily)
                .fontSize(12.px)
                .fontWeight(FontWeight.SemiBold)
                .color(Colors.Gray)
                .styleModifier { property("text-transform", "uppercase") }
                .letterSpacing(0.5.px),
        )
        Select(
            attrs = {
                style {
                    property("width", "100%")
                    property("height", "44px")
                    property("padding", "0 20px 0 14px")
                    property("background-color", "#ffffff")
                    property("border", "1px solid #d3d3d3")
                    property("border-radius", "10px")
                    property("outline", "none")
                    property("font-family", FontFamily.joinToString(", "))
                    property("font-size", "14px")
                    property("color", if (selected.isEmpty()) "#999" else "#262525")
                    property("cursor", "pointer")
                    property("appearance", "auto")
                }
                onChange { e ->
                    val v = e.target.asDynamic()?.value as? String ?: ""
                    if (v.isNotEmpty()) onSelected(v)
                }
            }
        ) {
            Option(value = "", attrs = { attr("disabled", ""); if (selected.isEmpty()) attr("selected", "") }) {
                Text("Select type…")
            }
            crudWallpaperTypes.forEach { t ->
                Option(value = t, attrs = { if (selected == t) attr("selected", "") }) { Text(t) }
            }
        }
    }
}
